import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  type SetupPosition,
  type SetupStepNavigator,
  backTarget,
  lastBackIndex,
  nextStepTarget,
  screenAt,
  screenIndex,
} from '@/lib/setup/setup-screen'
import { type SetupMotion, moveMotion, nextStepMotion } from '@/lib/setup/setup-motion'
import { type OnboardingChainStep, previousBuiltStep } from '@/types/onboarding-chain'

export const useSetupPositionStore = defineStore('setup-position', () => {
  const position = ref<SetupPosition | null>(null)

  const motion = ref<SetupMotion>('none')

  const canGoBack = computed(() => position.value !== null && backTarget(position.value) !== null)

  function show(target: SetupPosition): void {
    motion.value = 'none'
    position.value = target
  }

  function back(): void {
    const target = position.value === null ? null : backTarget(position.value)
    if (target === null) return
    motion.value = 'back'
    position.value = target
  }

  /** Moves back to the last screen Back may land on of the shipped step before the one on show. */
  function backToPreviousStep(): void {
    const previous = position.value === null ? null : previousBuiltStep(position.value.step)
    const index = previous === null ? null : lastBackIndex(previous)
    if (previous === null || index === null) return
    motion.value = 'back'
    position.value = { step: previous, index }
  }

  /** Moves between the screens of the step on show; does nothing while another step is on show. */
  function move<Screen extends string, Move extends string>(
    step: OnboardingChainStep,
    navigator: SetupStepNavigator<Screen, Move>,
    applied: Move,
  ): void {
    const current = position.value
    if (current?.step !== step) return
    const from = screenAt(navigator.screens, current.index)
    if (from === null) return
    const target = { step, index: screenIndex(navigator.screens, navigator.move(from, applied)) }
    if (target.index === current.index) return
    motion.value = moveMotion(current, target)
    position.value = target
  }

  /** Moves to the next shipped step; `false` on the last one. */
  function goToNextStep(openAt?: (step: OnboardingChainStep) => number): boolean {
    if (position.value === null) return false
    const target = nextStepTarget(position.value.step)
    if (target === 'finish') return false
    motion.value = nextStepMotion(position.value)
    position.value = { step: target.step, index: openAt?.(target.step) ?? target.index }
    return true
  }

  /** Moves straight to a screen of another step, as Continue does. */
  function jump(target: SetupPosition): void {
    motion.value = 'step'
    position.value = target
  }

  function reset(): void {
    motion.value = 'none'
    position.value = null
  }

  return {
    position,
    motion,
    canGoBack,
    show,
    back,
    backToPreviousStep,
    move,
    goToNextStep,
    jump,
    reset,
  }
})
